import React, { useState, useEffect, useCallback } from 'react';
import { X, Share2, Send, Users, Loader, Check, Trash2, FileSignature, AlertTriangle } from 'lucide-react';
import { documentService, clientService, casePartyService, signingService } from '../../services/api';
import useModal from '../../hooks/useModal';

const ShareDocumentModal = ({ document, caseId, isOpen, onClose }) => {
  const [clients, setClients] = useState([]);
  const [sharedWith, setSharedWith] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState('email');
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [unsharing, setUnsharing] = useState(null);
  const [error, setError] = useState('');
  const [inviting, setInviting] = useState(false);
  const [success, setSuccess] = useState('');
  const [sendForSignature, setSendForSignature] = useState(false);
  const [signingConfigured, setSigningConfigured] = useState(false);

  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);

  const loadData = useCallback(async () => {
    if (!document) return;
    setLoading(true);
    setError('');
    try {
      const promises = [
        documentService.getSharingStatus(document.id),
      ];
      if (caseId) {
        promises.push(casePartyService.getParties(caseId));
      }
      const [sharingRes, partiesRes] = await Promise.all(promises);
      setSharedWith(sharingRes.data.data || []);

      // Only case clients are eligible for sharing
      if (partiesRes) {
        const parties = (partiesRes.data || []).filter(p => p.role === 'client' && p.client);
        const caseClients = parties.map(p => p.client);
        setClients(caseClients);
        if (caseClients.length === 1) {
          setSelectedClientId(caseClients[0].id);
        }
      } else {
        setClients([]);
      }

      // Check if signing service is available (only for PDF documents)
      if (document.mime_type && document.mime_type.toLowerCase().includes('pdf')) {
        try {
          const statusRes = await signingService.getStatus();
          setSigningConfigured(statusRes.data?.configured === true);
        } catch {
          setSigningConfigured(false);
        }
      } else {
        setSigningConfigured(false);
      }
    } catch (err) {
      console.error('Failed to load sharing data:', err);
      setError('Failed to load client data');
    } finally {
      setLoading(false);
    }
  }, [document, caseId]);

  useEffect(() => {
    if (isOpen && document) {
      loadData();
      setSelectedClientId('');
      setNote('');
      setNotify('email');
      setSuccess('');
      setError('');
      setSendForSignature(false);
    }
  }, [isOpen, document, loadData]);

  const handleShare = async (e) => {
    e.preventDefault();
    if (!selectedClientId) return;

    setSharing(true);
    setError('');
    setSuccess('');
    try {
      await documentService.shareWithClient(document.id, selectedClientId, {
        note: note.trim() || undefined,
        notify,
      });
      const client = clients.find((c) => c.id === selectedClientId);
      const clientName = client
        ? `${client.first_name || ''} ${client.last_name || ''}`.trim() || client.company_name
        : 'client';

      // If send for signature is checked, create a signing envelope
      if (sendForSignature && client) {
        const signerName = `${client.first_name || ''} ${client.last_name || ''}`.trim() || client.company_name || 'Signer';
        const signerEmail = client.email;
        if (!signerEmail) {
          setError('Cannot send for signature: client has no email address');
          setSharing(false);
          return;
        }
        try {
          await signingService.createEnvelopeFromDocument({
            document_id: document.id,
            signers: [{ email: signerEmail, name: signerName, role: 'Signer' }],
            case_id: caseId || null,
            client_id: client.id,
            send_email: true,
            name: document.original_file_name,
            subject: `Please sign: ${document.original_file_name}`,
            message: note.trim() || undefined,
          });
          setSuccess(`Document shared with ${clientName} and sent for signature`);
        } catch (sigErr) {
          const sigDetail = sigErr.response?.data?.detail;
          setError(sigDetail || 'Document shared but failed to send for signature');
          setSharing(false);
          return;
        }
      } else {
        setSuccess(`Document shared with ${clientName}`);
      }

      setSelectedClientId('');
      setNote('');
      setSendForSignature(false);
      // Refresh sharing status
      const sharingRes = await documentService.getSharingStatus(document.id);
      setSharedWith(sharingRes.data.data || []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Failed to share document');
    } finally {
      setSharing(false);
    }
  };

  const handleUnshare = async (clientId) => {
    setUnsharing(clientId);
    setError('');
    try {
      await documentService.unshareWithClient(document.id, clientId);
      setSharedWith((prev) => prev.filter((s) => s.client_id !== clientId));
      setSuccess('Document unshared');
    } catch (err) {
      setError('Failed to unshare document');
    } finally {
      setUnsharing(null);
    }
  };

  const handleSendPortalInvite = async (clientId) => {
    setInviting(true);
    setError('');
    try {
      await clientService.sendPortalInvite(clientId);
      // Update local client to reflect portal access
      setClients(prev => prev.map(c =>
        c.id === clientId ? { ...c, has_portal_access: true } : c
      ));
      setSuccess('Portal invitation sent');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send portal invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleClose = () => {
    if (!sharing) {
      onClose();
    }
  };

  if (!isOpen || !document) return null;

  // Filter out clients already shared with
  const sharedClientIds = new Set(sharedWith.map((s) => s.client_id));
  const availableClients = clients.filter((c) => !sharedClientIds.has(c.id));

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Share2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Share Document</h3>
              <p className="text-sm text-gray-500 truncate max-w-[280px]" title={document.original_file_name}>
                {document.original_file_name}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={sharing}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              <Check className="h-4 w-4 mr-2 flex-shrink-0" />
              {success}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Share form */}
              <form onSubmit={handleShare} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Share with client
                  </label>
                  {availableClients.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">
                      {clients.length === 0 ? 'No clients associated with this case' : 'Already shared with all case clients'}
                    </p>
                  ) : availableClients.length === 1 ? (
                    <p className="text-sm font-medium text-gray-900 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      {`${availableClients[0].first_name || ''} ${availableClients[0].last_name || ''}`.trim() || availableClients[0].company_name || 'Unknown'}
                      {availableClients[0].email && <span className="text-gray-500 ml-1">({availableClients[0].email})</span>}
                    </p>
                  ) : (
                    <select
                      value={selectedClientId}
                      onChange={(e) => setSelectedClientId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a client...</option>
                      {availableClients.map((c) => {
                        const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.company_name || 'Unknown';
                        const email = c.email ? ` (${c.email})` : '';
                        return (
                          <option key={c.id} value={c.id}>
                            {name}{email}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>

                {selectedClientId && (() => {
                  const selectedClient = clients.find(c => c.id === selectedClientId);
                  if (selectedClient && selectedClient.has_portal_access === false) {
                    return (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-amber-800">
                              This client doesn't have portal access yet
                            </p>
                            <p className="text-xs text-amber-700 mt-0.5">
                              They won't be able to view this document until they're invited to the client portal.
                            </p>
                            <button
                              type="button"
                              onClick={() => handleSendPortalInvite(selectedClientId)}
                              disabled={inviting || !selectedClient.email}
                              className="mt-2 inline-flex items-center px-3 py-1 text-xs font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded hover:bg-amber-200 disabled:opacity-50 transition-colors"
                            >
                              {inviting ? (
                                <><Loader className="h-3 w-3 mr-1 animate-spin" /> Sending...</>
                              ) : !selectedClient.email ? (
                                'No email address — cannot invite'
                              ) : (
                                <><Send className="h-3 w-3 mr-1" /> Send Portal Invitation</>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notification method
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="notify"
                        value="email"
                        checked={notify === 'email'}
                        onChange={(e) => setNotify(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Email notification</span>
                        <p className="text-xs text-gray-500">Client receives an email with a link to the portal</p>
                      </div>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="notify"
                        value="none"
                        checked={notify === 'none'}
                        onChange={(e) => setNotify(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Quiet share</span>
                        <p className="text-xs text-gray-500">Document appears in portal without notification</p>
                      </div>
                    </label>
                  </div>
                </div>

                {signingConfigured && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendForSignature}
                        onChange={(e) => setSendForSignature(e.target.checked)}
                        className="mt-0.5 text-yellow-600 focus:ring-yellow-500 rounded"
                        disabled={sharing}
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900 flex items-center">
                          <FileSignature className="h-4 w-4 mr-1.5 text-yellow-600" />
                          Send for Signature
                        </span>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Also send this document for e-signature via JustSign
                        </p>
                      </div>
                    </label>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Note <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g., Please review and sign by Friday..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows={2}
                    maxLength={500}
                    disabled={sharing}
                  />
                </div>

                <button
                  type="submit"
                  disabled={!selectedClientId || sharing}
                  className={`w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                    sendForSignature
                      ? 'bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-500'
                      : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                  }`}
                >
                  {sharing ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      {sendForSignature ? 'Sharing & Sending...' : 'Sharing...'}
                    </>
                  ) : (
                    <>
                      {sendForSignature ? (
                        <FileSignature className="h-4 w-4 mr-2" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      {sendForSignature ? 'Share & Send for Signature' : 'Share Document'}
                    </>
                  )}
                </button>
              </form>

              {/* Currently shared with */}
              {sharedWith.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <Users className="h-4 w-4 mr-1.5" />
                    Currently shared with
                  </h4>
                  <div className="space-y-2">
                    {sharedWith.map((share) => (
                      <div
                        key={share.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {share.client_name}
                          </p>
                          {share.client_email && (
                            <p className="text-xs text-gray-500 truncate">{share.client_email}</p>
                          )}
                          <p className="text-xs text-gray-400">
                            Shared {new Date(share.shared_at).toLocaleDateString()}
                            {share.note && ` — ${share.note}`}
                          </p>
                        </div>
                        <button
                          onClick={() => handleUnshare(share.client_id)}
                          disabled={unsharing === share.client_id}
                          className="ml-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 flex-shrink-0"
                          title="Remove sharing"
                        >
                          {unsharing === share.client_id ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={sharing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareDocumentModal;
